import { useState, useEffect, useRef } from "react";
import { doc, updateDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";
import TimeSelector from "./TimeSelector";
import { SEGMENT_TYPES } from "../utils";

async function rebuildSearchText(tripId) {
  try {
    const segsSnap = await getDocs(collection(db, "trips", tripId, "segments"));
    const text = segsSnap.docs.map(d => {
      const s = d.data();
      return [s.title, s.address, s.detail, s.confirmation, s.phone].filter(Boolean).join(" ");
    }).join(" ").toLowerCase();
    await updateDoc(doc(db, "trips", tripId), { searchText: text });
  } catch (e) {
    console.warn("searchText update failed", e);
  }
}

export default function AddSegmentModal({ onClose, onAdd, segments, prefillDate, prefillCity, tripStartDate, tripEndDate, tripId }) {
  const [type, setType] = useState("flight");
  const [date, setDate] = useState(prefillDate || "");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [time, setTime] = useState("");
  const [title, setTitle] = useState("");
  const [address, setAddress] = useState("");
  const [detail, setDetail] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [link, setLink] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState(prefillCity || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [chosenLocation, setChosenLocation] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  // When switching to a location-based type, restore prefillCity if city is empty
  useEffect(() => {
    if (needsLocation && !city && prefillCity) setCity(prefillCity);
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const isHotel = type === "hotel";
  const needsLocation = ["hotel", "restaurant", "activity"].includes(type);

  useEffect(() => {
    if (!needsLocation || !searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const scopedQuery = city ? `${searchQuery} ${city}` : searchQuery;
        const response = await fetch("/api/places", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: scopedQuery }),
        });
        const data = await response.json();
        setSuggestions(data.results || []);
      } catch {
        setSuggestions([]);
      }
      setSearching(false);
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [searchQuery, needsLocation, city]);

  const handleSelectSuggestion = (s) => {
    setTitle(s.name);
    setAddress(s.address);
    setSearchQuery(s.name);
    setChosenLocation({ lat: s.lat, lng: s.lng });
    if (s.website) setLink(s.website);
    if (s.phone) setPhone(s.phone);
    setSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!title || !date) return;
    setSaving(true);
    await onAdd({
      type, title, address, detail, date, time,
      checkoutDate: isHotel ? checkoutDate : "",
      confirmation, link, phone,
      city: needsLocation ? city : "",
      location: chosenLocation || null,
    });
    // Rebuild search index on the trip document
    if (tripId) await rebuildSearchText(tripId);
    setSaving(false);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Add segment</div>
          <button className="modal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        {/* Type */}
        <div className="form-group">
          <label className="form-label">Type</label>
          <div className="type-selector">
            {Object.entries(SEGMENT_TYPES).map(([key, cfg]) => (
              <button key={key} className={`type-btn ${type === key ? "selected" : ""}`} onClick={() => setType(key)}>
                <Icon name={cfg.icon} size={12} />{cfg.label}
              </button>
            ))}
          </div>
        </div>

        {/* City — for location-based segments */}
        {needsLocation && (
          <div className="form-group">
            <label className="form-label">City</label>
            <input
              className="form-input"
              placeholder="e.g. Rome, Tokyo, New York"
              value={city}
              onChange={(e) => { setCity(e.target.value); setSuggestions([]); }}
            />
            {city && (
              <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>
                Search results will be scoped to this city
              </div>
            )}
          </div>
        )}

        {/* Dates */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{isHotel ? "Check-in" : "Date"}</label>
            <input className="form-input" type="date" value={date} min={tripStartDate || undefined} max={tripEndDate || undefined} onChange={(e) => setDate(e.target.value)} />
          </div>
          {isHotel ? (
            <div className="form-group">
              <label className="form-label">Check-out</label>
              <input className="form-input" type="date" value={checkoutDate} min={date || undefined} max={tripEndDate || undefined} onChange={(e) => setCheckoutDate(e.target.value)} />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Time</label>
              <TimeSelector value={time} onChange={setTime} segmentType={type} />
            </div>
          )}
        </div>

        {/* Search for location-based segments */}
        {needsLocation && (
          <div className="form-group">
            <label className="form-label">Search</label>
            <div className="location-wrapper">
              <input
                className="form-input"
                placeholder={`Search for a ${type} anywhere...`}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setTitle(e.target.value); setChosenLocation(null); }}
                style={{ paddingRight: searching ? 90 : 12 }}
              />
              {searching && (
                <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "var(--ink-light)" }}>
                  Searching...
                </div>
              )}
              {suggestions.length > 0 && (
                <div className="location-suggestions">
                  {suggestions.map((s, i) => (
                    <div key={i} className="location-suggestion" onClick={() => handleSelectSuggestion(s)}>
                      <div className="suggestion-name">{s.name}</div>
                      <div className="suggestion-addr">{s.address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Title */}
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            className="form-input"
            placeholder={type === "flight" ? "JFK -> CDG" : type === "train" ? "Rome -> Florence" : `${SEGMENT_TYPES[type]?.label || "Item"} name`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Address for location-based segments */}
        {needsLocation && (
          <div className="form-group">
            <label className="form-label">Address</label>
            <input
              className="form-input"
              placeholder="Street address, City, Country"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
        )}

        {/* Details */}
        <div className="form-group">
          <label className="form-label">Details</label>
          <input
            className="form-input"
            placeholder={
              type === "flight" ? "Delta DL 401 · Nonstop · 9h" :
              type === "train" ? "Trenitalia · 1h 30m" :
              type === "hotel" ? "Room type, notes" :
              type === "restaurant" ? "Cuisine, notes" :
              "Additional details"
            }
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
          />
        </div>

        {/* Confirmation & Phone */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Confirmation #</label>
            <input className="form-input" placeholder="e.g. DL4X9Q" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" placeholder="+1 234 567 8900" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Website</label>
          <input className="form-input" placeholder="https://..." value={link} onChange={(e) => setLink(e.target.value)} />
        </div>

        <button className="submit-btn" onClick={handleSubmit} disabled={!title || !date || saving}>
          {saving ? "Saving..." : "Add to itinerary"}
        </button>
      </div>
    </div>
  );
}
